import React, { useContext, useEffect, useState } from "react";
import {
  Row,
  Col,
  Button,
  Typography,
  Empty,
  Input,
} from "antd";
import InvestigationForm from "./InvestigationForm";
import BulkUploadModal from "../../common/BulkUploadModal";
import { prepareInvestigationBulkUploadFile } from "../../../helpers/bulkUploadExcel";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import SideMenu from "../../Layout/CustomLayout/SideMenu";
import btnStyles from "../../../styles/Custom/Button.module.css";
import { CloudUploadOutlined, PlusOutlined } from "@ant-design/icons";
import CustomLayout from "../../Layout/CustomLayout";
import { debounce } from "../../../helpers/utility";
import {
  getFacilityInvestigationsApi,
  getInvestigationsApi,
  uploadInvestigationsApi,
} from "../../../services/investigation.services";
import InvestigationsTable from "./InvestigationsTable";
import InvestigationsPagination from "./InvestigationsPagination";
import styles from "./Investigations.module.css";
import { UserContext } from "../../../context/UserContext";
import { useNavigate } from "react-router-dom";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";

const PAGE_SIZE = 10;

const Investigations = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const { userDetails } = useContext(UserContext);
  const [isVisible, setIsVisible] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(() => parsePageParam(params));
  const [searchQuery, setSearchQuery] = useState(() => parseSearchParam(params));

  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const isOrgAdmin = defaultRole !== "Facility_Admin";
  const hasInvestigations = data?.message?.length > 0;

  useEffect(() => {
    let queryParams = { page: page, limit: PAGE_SIZE };
    if (searchQuery) {
      queryParams.search = searchQuery;
      queryParams.page = 1;
    }
    const queryString = new URLSearchParams(queryParams);
    setLoading(true);
    if (defaultRole !== "Facility_Admin") {
      getInvestigationsApi(queryString)
        .then((res) => {
          setData(res?.data);
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setData([]);
          setLoading(false);
        });
    }
  }, [refresh, page, searchQuery]);

  useEffect(() => {
    let queryParams = { page: page, limit: PAGE_SIZE };
    if (searchQuery) {
      queryParams.search = searchQuery;
      queryParams.page = 1;
    }
    const queryString = new URLSearchParams(queryParams);
    if (defaultRole === "Facility_Admin") {
      getFacilityInvestigationsApi(
        Number(getCookie(FacilityID)) || userDetails?.currFacility,
        queryString
      )
        .then((res) => {
          setData(res?.data);
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setData([]);
          setLoading(false);
        });
    }
  }, [
    params?.facility,
    userDetails?.currFacility,
    page,
    defaultRole,
    searchQuery,
  ]);

  const onInvSearch = (value) => {
    const query = value?.target?.value;
    setPage(1);
    setSearchQuery(query || null);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setPage(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };

  const renderActionButtons = () => (
    <div className={styles.headerActions}>
      <Button
        className={`${btnStyles.lgBtn} ${styles.uploadBtn}`}
        icon={<CloudUploadOutlined />}
        onClick={() => setUploadModalOpen(true)}
      >
        Upload Excel
      </Button>
      <Button
        type="primary"
        className={btnStyles.lgBtn}
        icon={<PlusOutlined />}
        onClick={() => setIsVisible(true)}
      >
        Add Investigation
      </Button>
    </div>
  );

  return (
    <CustomLayout
      header={<Typography className={typoStyles.h6}>Investigations</Typography>}
      sideContent={<SideMenu />}
    >
      <>
        <Row gutter={[12, 12]}>
          {isOrgAdmin && (
            <>
              <Col span={12}>
                <Input.Search
                  style={{ width: "80%", marginBottom: "10px" }}
                  size="large"
                  placeholder="Search investigation"
                  onChange={debounce(onInvSearch, 300)}
                />
              </Col>
              <Col span={12}>{renderActionButtons()}</Col>
            </>
          )}

          {!isOrgAdmin && (hasInvestigations || searchQuery) && (
            <Col span={12}>
              <Input.Search
                style={{ width: "80%", marginBottom: "10px" }}
                size="large"
                placeholder="Search investigation"
                onChange={debounce(onInvSearch, 300)}
              />
            </Col>
          )}

          <Col span={24}>
            {defaultRole === "Facility_Admin" && !hasInvestigations && !searchQuery ? (
              <Empty />
            ) : (
              <div className={`${styles.tableCard} ui-table-fill-shell`}>
                <InvestigationsTable
                  data={data?.message}
                  refreshPage={refreshPage}
                  userDetails={userDetails}
                  loading={loading}
                />
                <InvestigationsPagination
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={data?.total_count}
                  onChange={handlePageChange}
                />
              </div>
            )}
          </Col>
        </Row>

        {isVisible && (
          <InvestigationForm
            open={isVisible}
            setOpen={setIsVisible}
            refreshPage={refreshPage}
          />
        )}

        {uploadModalOpen && (
          <BulkUploadModal
            open={uploadModalOpen}
            setOpen={setUploadModalOpen}
            refreshPage={refreshPage}
            title="Bulk Upload Investigations"
            requiredColumns={[
              "Name",
              "Description",
              "Cash Price",
              "Cash Withtax",
              "Insurance Price",
              "Insurance Withtax",
              "Tax",
            ]}
            sampleFileName="bulk_import_curengo_investigation_sample.xlsx"
            uploadApi={uploadInvestigationsApi}
            fileField="investigations"
            prepareUploadFile={prepareInvestigationBulkUploadFile}
          />
        )}
      </>
    </CustomLayout>
  );
};

export default Investigations;
